import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { DatePicker, Spin, Button, message, Select, Modal } from 'antd';
import dayjs from 'dayjs';
import 'antd/dist/reset.css';
import { fetchActivitiesByLocation, fetchPlanVersions, fetchPlannerDocument, fetchFileFromBlob, fetchEquipmentList } from './api';
import { PRODUCT_YIELD, DEFAULT_YIELD } from './constants/productYield';

const { RangePicker } = DatePicker;

const ConsolidatedTracker = () => {
 
  
  // Calculate default date/time range dynamically (same as Equipment Log)
  // Round current time UP to next hour, then go back 23 hours
  const calculateDefaultRange = () => {
    const now = new Date();
    const rounded = new Date(now);
    
    // Round UP to next hour (ceiling)
    if (rounded.getMinutes() > 0 || rounded.getSeconds() > 0 || rounded.getMilliseconds() > 0) {
      rounded.setHours(rounded.getHours() + 1);
    }
    rounded.setMinutes(0, 0, 0);
    
    const end = dayjs(rounded);
    const start = end.subtract(23, 'hour');
    
    return [start, end];
  };
  
  const [dateTimeRange, setDateTimeRange] = useState(() => calculateDefaultRange());
  const [filterProject, setFilterProject] = useState('all');
  const [data, setData] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]); // Full equipment list from API
  const [apiSummary, setApiSummary] = useState(null); // Store API summary response
  const [apiMeta, setApiMeta] = useState(null); // Store API meta response
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Plan version state
  const [planVersions, setPlanVersions] = useState([]);
  const [selectedPlanVersion, setSelectedPlanVersion] = useState(null); // null means latest
  const [loadingVersions, setLoadingVersions] = useState(false);
  
  // PDF loading state
  const [loadingPdf, setLoadingPdf] = useState(false);
  
  // PDF list modal state
  const [pdfListModalVisible, setPdfListModalVisible] = useState(false);
  const [pdfList, setPdfList] = useState([]);
  const [currentBatchInfo, setCurrentBatchInfo] = useState({ batchNumber: '', equipmentCode: '' });
  const [openingPdfIndex, setOpeningPdfIndex] = useState(null); // Track which PDF is being opened

  // Format date for display (matches Equipment Log page)
  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = dayjs.isDayjs(date) ? date.toDate() : (date instanceof Date ? date : dayjs(date).toDate());
    return dateObj.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Format time for display (matches Equipment Log page - 24-hour format)
  const formatTime = (date) => {
    if (!date) return '';
    const dateObj = dayjs.isDayjs(date) ? date.toDate() : (date instanceof Date ? date : dayjs(date).toDate());
    return dateObj.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Transform API response to table format
  // Uses equipment list as PRIMARY source (same as PhysicalLogEntry)
  const transformApiData = (apiResponse, fullEquipmentList) => {
    // Build activity data map from API response
    const equipmentData = apiResponse?.data || apiResponse || {};
    const activityMap = {};
    
    if (equipmentData && typeof equipmentData === 'object') {
      for (const [equipmentName, equipmentInfo] of Object.entries(equipmentData)) {
        if (!equipmentInfo || typeof equipmentInfo !== 'object') continue;
        activityMap[equipmentName] = equipmentInfo;
      }
    }
    
    // Transform activity info for a given equipment
    const transformEquipmentActivities = (equipmentInfo, eqpName) => {
      if (!equipmentInfo) return { activities: [], matchedCount: 0, notStartedCount: 0, unplannedCount: 0, unutilizedTime: null, productionVariance: null, productionVolumeVariance: null };
      
      const activityTimeline = equipmentInfo.activity_timeline || [];
      const matched = equipmentInfo.matched || [];
      const notStarted = equipmentInfo.not_started || [];
      const unplanned = equipmentInfo.unplanned || [];
      const unutilizedTime = equipmentInfo.unutilized_time || null;
      const productionVariance = equipmentInfo.production_variance || null;
      const productionVolumeVariance = equipmentInfo.production_volume_variance || null;
      
      const allActivities = activityTimeline.length > 0 
        ? activityTimeline 
        : [...matched, ...notStarted, ...unplanned];
      
      const rangeStart = dateTimeRange[0];
      const rangeEnd = dateTimeRange[1];

      const transformedActivities = allActivities.map(activity => {
        const status = activity.status;

        const planStartDayjs = activity.plan_start_date ? dayjs(activity.plan_start_date) : null;
        const planEndDayjs = activity.plan_end_date ? dayjs(activity.plan_end_date) : null;
        const isPlanInRange = (() => {
          if (!planStartDayjs && !planEndDayjs) return false;
          const startInRange = planStartDayjs
            && !planStartDayjs.isBefore(rangeStart) && !planStartDayjs.isAfter(rangeEnd);
          const endInRange = planEndDayjs
            && !planEndDayjs.isBefore(rangeStart) && !planEndDayjs.isAfter(rangeEnd);
          return !!(startInRange || endInRange);
        })();
        
        let planProduct = '';
        if (status === 'unplanned') {
          planProduct = '';
        } else if (activity.plan_product) {
          planProduct = activity.plan_product;
        } else if (activity.plan_activity) {
          planProduct = activity.plan_activity;
        }
        
        let actualProduct = '';
        if (activity.cleaning_type) {
          actualProduct = activity.cleaning_type;
        } else if (activity.actual_product) {
          actualProduct = activity.actual_product;
        }
        
        return {
          id: activity.plan_id || activity.actual_id,
          status: status,
          batchMatch: activity.batch_match,
          planProduct: planProduct,
          planProductName: activity.plan_product_name || '',
          actualProduct: actualProduct,
          productName: activity.product_name || '',
          planHrs: activity.sliced_plan_hrs || '',
          actualHrs: activity.actual_end_time === 'running' ? 'running' : (activity.sliced_actual_hrs || ''),
          variance: activity.variance,
          activityType: activity.activity_type,
          cleaningType: activity.cleaning_type,
          actualStartTime: activity.actual_start_time,
          actualEndTime: activity.actual_end_time,
          planStartDate: activity.plan_start_date,
          planEndDate: activity.plan_end_date,
          isOngoing: !activity.actual_end_time && activity.actual_start_time,
          equipmentName: activity.equipment_name,
          planActivity: activity.plan_activity,
          plannedProductATreportUrl: activity?.plannedProductATreportUrl || [],
          actualProductATreportUrl:activity?.actualProductATreportUrl || [],
          ...(() => {
            const isRunning = activity.actual_end_time === 'running';
            const _prod = (activity.plan_product_name || activity.product_name || '').toUpperCase().trim();
            const _eq = String(eqpName || '').toUpperCase();
            let _div = 1;
            if (_prod === 'ESO' && _eq.startsWith('FBP')) _div = 3;
            else if (_prod === 'BUP' && _eq.startsWith('COAT')) _div = 3;
            else if (_prod === 'PANTO' && _eq.startsWith('COAT')) _div = 2;
            else if (_prod === 'ESC' && /^B\d/.test(_eq)) _div = 2;
            const _yield = PRODUCT_YIELD[_prod] !== undefined ? PRODUCT_YIELD[_prod] : DEFAULT_YIELD;
            const rawPlan = activity.sliced_planned_volume;
            const plannedVolume = rawPlan != null ? Math.round((parseFloat(rawPlan) / _div) * _yield * 100) / 100 : rawPlan;
            if (isRunning) {
              return { plannedVolume, actualVolume: 'Running', volumeVariance: '-' };
            }
            const actualVolume = activity.sliced_actual_volume != null ? Math.round((parseFloat(activity.sliced_actual_volume) / _div) * _yield * 100) / 100 : activity.sliced_actual_volume;
            const volumeVariance = (actualVolume != null && plannedVolume != null)
              ? Math.round((actualVolume - plannedVolume) * 100) / 100
              : (activity.sliced_volume_variance != null ? Math.round(parseFloat(activity.sliced_volume_variance) * _yield * 100) / 100 : activity.sliced_volume_variance);
            return { plannedVolume, actualVolume, volumeVariance };
          })(),
          timeVariance: activity.actual_end_time === 'running' ? '-' : (activity.sliced_time_variance || ''),
          isPlanInRange: isPlanInRange
        };
      });
      
      return {
        activities: transformedActivities,
        matchedCount: matched.length,
        notStartedCount: notStarted.length,
        unplannedCount: unplanned.length,
        unutilizedTime,
        productionVariance,
        productionVolumeVariance
      };
    };
    
    // Use equipment list as PRIMARY source (exactly like PhysicalLogEntry)
    // First, transform and sort equipment list using PhysicalLogEntry's exact logic
    const sortedEquipment = (fullEquipmentList || [])
      .map(eq => {
        const eqpSequence = eq.eqp_sequence !== undefined && eq.eqp_sequence !== null 
          ? Number(eq.eqp_sequence) 
          : null;
        
        return { 
          id: eq.equipment_id || eq.equipment_name || eq.id,
          name: eq.equipment_name || eq.name || eq.equipment_id || eq.id,
          equipmentId: eq.equipment_id || eq.equipment_name || eq.id,
          eqp_sequence: eqpSequence
        };
      })
      .sort((a, b) => {
        // Exact same sorting logic as PhysicalLogEntry
        if (a.eqp_sequence !== null && b.eqp_sequence !== null) {
          return a.eqp_sequence - b.eqp_sequence;
        }
        if (a.eqp_sequence !== null) return -1;
        if (b.eqp_sequence !== null) return 1;
        const nameA = a?.name != null ? String(a.name) : '';
        const nameB = b?.name != null ? String(b.name) : '';
        return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
      });
    
    // Map activity data onto sorted equipment list
    const result = sortedEquipment.map(eq => {
      const equipmentName = eq.name;
      const equipmentInfo = activityMap[equipmentName];
      const activityData = transformEquipmentActivities(equipmentInfo, equipmentName);
      
      return {
        project: equipmentName,
        equipmentCodeNumber: eq.equipmentId,
        unutilizedTime: activityData.unutilizedTime,
        productionVariance: activityData.productionVariance,
        productionVolumeVariance: activityData.productionVolumeVariance,
        eqpSequence: eq.eqp_sequence,
        activities: activityData.activities,
        matchedCount: activityData.matchedCount,
        notStartedCount: activityData.notStartedCount,
        unplannedCount: activityData.unplannedCount
      };
    });
    
    return result;
  };

  // Fetch data from API
  const fetchData = useCallback(async () => {
    if (!dateTimeRange || !dateTimeRange[0] || !dateTimeRange[1]) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const startDate = dateTimeRange[0].format('YYYY-MM-DD HH:mm:ss');
      const endDate = dateTimeRange[1].format('YYYY-MM-DD HH:mm:ss');
      
      // Build API params - only include plan_version if a specific version is selected
      const apiParams = {
        start_date: startDate,
        end_date: endDate
      };
      
      if (selectedPlanVersion !== null) {
        apiParams.plan_version = selectedPlanVersion;
      }
      
      const response = await fetchActivitiesByLocation(apiParams);
      
      const transformedData = transformApiData(response, equipmentList);
      setData(transformedData);
      
      // Extract and store summary from API response
      if (response && response.summary) {
        setApiSummary(response.summary);
      }
      
      // Extract and store meta from API response
      if (response && response.meta) {
        setApiMeta(response.meta);
      }
    } catch (err) {
      setError(err.message);
      // console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [dateTimeRange, selectedPlanVersion, equipmentList]);

  // Fetch equipment list on mount (same as PhysicalLogEntry)
  useEffect(() => {
    const loadEquipmentList = async () => {
      try {
        const response = await fetchEquipmentList();
        setEquipmentList(response.data || []);
      } catch (err) {
        // console.error('Error loading equipment list:', err);
      }
    };
    loadEquipmentList();
  }, []);

  // Fetch plan versions on mount
  const loadPlanVersions = useCallback(async () => {
    setLoadingVersions(true);
    try {
      const response = await fetchPlanVersions();
      if (response && response.data) {
        setPlanVersions(response.data);
      }
    } catch (err) {
      // console.error('Error fetching plan versions:', err);
    } finally {
      setLoadingVersions(false);
    }
  }, []);

  // Fetch plan versions on mount
  useEffect(() => {
    loadPlanVersions();
  }, [loadPlanVersions]);

  // Fetch data on mount and when date range or plan version changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getVarianceStyle = (variance) => {
    if (variance === null || variance === undefined || variance === "" || variance === "-") {
      return {};
    }
    // Handle both string and number variances
    const numVariance = typeof variance === 'string' ? parseFloat(variance) : variance;
    if (isNaN(numVariance)) return {};
    
    if (numVariance > 0) return { backgroundColor: '#fee2e2', color: '#991b1b', fontWeight: '600' };
    if (numVariance < 0) return { backgroundColor: '#dcfce7', color: '#166534', fontWeight: '600' };
    if (numVariance === 0) return { backgroundColor: '#fef3c7', color: '#92400e', fontWeight: '600' };
    return {};
  };

  const formatVariance = (variance) => {
    if (variance === null || variance === undefined || variance === "") return '';
    if (variance === "-") return "-";
    if (variance === 0 || variance === "0") return '0';
    return variance;
  };

  // Use API summary if available, otherwise calculate from data
  const summary = useMemo(() => {
    if (apiSummary) {
      // Use API summary with new format
      return {
        totalActivities: apiSummary.total_activities || 0,
        totalMatched: apiSummary.total_matched || 0,
        totalNotStarted: apiSummary.total_not_started || 0,
        totalUnplanned: apiSummary.total_unplanned || 0,
        avgVariance: apiSummary.avg_variance || '0.00 hrs'
      };
    }
    
    // Fallback: calculate from data if API summary not available
    let totalActivities = 0;
    let totalMatched = 0;
    let totalNotStarted = 0;
    let totalUnplanned = 0;

    data.forEach(project => {
      totalMatched += project.matchedCount || 0;
      totalNotStarted += project.notStartedCount || 0;
      totalUnplanned += project.unplannedCount || 0;
      totalActivities += project.activities?.length || 0;
    });

    return {
      totalActivities,
      totalMatched,
      totalNotStarted,
      totalUnplanned,
      avgVariance: '0.00 hrs'
    };
  }, [data, apiSummary]);

  const filteredData = useMemo(() => {
    if (filterProject === 'all') return data;
    return data.filter(project => {
      const projectName = project.project.toLowerCase();
      if (filterProject === 'compression') return projectName.includes('compression');
      if (filterProject === 'coating') return projectName.includes('coating');
      return true;
    });
  }, [data, filterProject]);

  // Equipment category mapping based on name prefix
  const getEquipmentCategory = (equipmentName) => {
    if (!equipmentName) return 'Other';
    const name = String(equipmentName).toUpperCase();
    
    // Order matters - check more specific patterns first
    if (name.startsWith('COMP-') || name.startsWith('COMP')) return 'Compression';
    if (name.startsWith('COAT-') || name.startsWith('COAT')) return 'Coating';
    if (name.startsWith('CAPS-') || name.startsWith('CAPS')) return 'Capsulation';
    if (name.startsWith('IMP-') || name.startsWith('IMP')) return 'Imprinting';
    if (name.startsWith('FBP-') || name.startsWith('FBP')) return 'FBP';
    if (/^P\d+\s*(PRIMARY|SECONDARY)$/.test(name)) return 'Packing';
    if (name.match(/^B\d/)) return 'Blending'; // B1, B2, B3, etc.
    if (name.match(/^G\d/)) return 'Granulation'; // G1, G2, G3, etc.
    return 'Other';
  };

  // Group configuration with colors and display order
  const groupConfig = {
    'Granulation': { color: '#8b5cf6', bgColor: '#f5f3ff', borderColor: '#c4b5fd', icon: '🧪' },
    'FBP': { color: '#06b6d4', bgColor: '#ecfeff', borderColor: '#67e8f9', icon: '🌡️' },
    'Blending': { color: '#f59e0b', bgColor: '#fffbeb', borderColor: '#fcd34d', icon: '🔄' },
    'Compression': { color: '#3b82f6', bgColor: '#eff6ff', borderColor: '#93c5fd', icon: '⚙️' },
    'Coating': { color: '#10b981', bgColor: '#ecfdf5', borderColor: '#6ee7b7', icon: '🎨' },
    'Capsulation': { color: '#ec4899', bgColor: '#fdf2f8', borderColor: '#f9a8d4', icon: '💊' },
    'Imprinting': { color: '#6366f1', bgColor: '#eef2ff', borderColor: '#a5b4fc', icon: '✍️' },
    'Packing': { color: '#f97316', bgColor: '#fff7ed', borderColor: '#fdba74', icon: '📦' },
    'Other': { color: '#6b7280', bgColor: '#f9fafb', borderColor: '#d1d5db', icon: '📦' }
  };

  // Define group order
  const groupOrder = ['Granulation', 'FBP', 'Blending', 'Compression', 'Coating', 'Capsulation', 'Imprinting', 'Packing', 'Other'];

  // Group equipment by category
  const groupedData = useMemo(() => {
    const groups = {};
    
    filteredData.forEach(equipment => {
      const category = getEquipmentCategory(equipment.project);
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(equipment);
    });
    
    // Return ordered groups (only those with equipment)
    return groupOrder
      .filter(category => groups[category] && groups[category].length > 0)
      .map(category => ({
        category,
        config: groupConfig[category],
        equipment: groups[category]
      }));
  }, [filteredData]);



  // Calculate total hours from date range
  const totalHours = useMemo(() => {
    if (!dateTimeRange || !dateTimeRange[0] || !dateTimeRange[1]) return null;
    const start = dateTimeRange[0];
    const end = dateTimeRange[1];
    const hoursDiff = end.diff(start, 'hour', true);
    return Math.round(hoursDiff * 100) / 100; // Round to 2 decimal places
  }, [dateTimeRange]);

  // Export to Excel function
  const handleExportToExcel = useCallback(async () => {
    if (!dateTimeRange || !dateTimeRange[0] || !dateTimeRange[1]) {
      message.warning('Please select a date range before exporting');
      return;
    }

    setExporting(true);
    try {
      // Build CSV header
      const headers = [
        'Equipment',
        'Unaccounted Time (hrs)',
        'Plan Batch',
        'Actual Batch',
        'Plan Hrs',
        'Actual Hrs',
        'Time Diff (hrs)',
        'Plan Vol',
        'Actual Vol',
        'Vol Diff',
        'Status',
        'Remarks',
        'Plan Start Time',
        'Plan End Time',
        'Actual Start Time',
        'Actual End Time',
        'Activity Type'
      ];
      
      const csvRows = [];
      csvRows.push(headers.join(','));
      
      // Add date range info as first row
      const startDateStr = dateTimeRange[0].format('DD-MMM-YYYY HH:mm');
      const endDateStr = dateTimeRange[1].format('DD-MMM-YYYY HH:mm');
      csvRows.push(`Date Range: ${startDateStr} to ${endDateStr}`);
      csvRows.push(''); // Empty row for spacing
      csvRows.push(headers.join(',')); // Headers again after date info
      
      // Helper function to escape CSV values
      const escapeCsv = (value) => {
        if (value === null || value === undefined) return '';
        const str = String(value);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };
      
      // Helper to format time variance
      const formatTimeVariance = (variance) => {
        if (variance === null || variance === undefined || variance === '') return '';
        const num = parseFloat(variance);
        if (isNaN(num)) return '';
        return num > 0 ? `+${num}` : num.toString();
      };
      
      // Helper to format volume variance
      const formatVolumeVariance = (variance) => {
        if (variance === null || variance === undefined || variance === '') return '';
        const num = parseFloat(variance);
        if (isNaN(num)) return '';
        return num > 0 ? `+${num}` : num.toString();
      };
      
      // Iterate through filtered data (respects current project filter)
      const dataToExport = filterProject === 'all' 
        ? data 
        : data.filter(row => row.project === filterProject);
      
      dataToExport.forEach(equipment => {
        const equipmentName = equipment.project;
        const unutilizedTime = equipment.unutilizedTime 
          ? `${equipment.unutilizedTime}` 
          : '';
        
        // If equipment has activities, export each activity as a row
        if (equipment.activities && equipment.activities.length > 0) {
          equipment.activities.forEach((activity, idx) => {
            const row = [
              escapeCsv(idx === 0 ? equipmentName : ''), // Equipment name only on first row
              escapeCsv(idx === 0 ? unutilizedTime : ''), // Unaccounted time only on first row
              escapeCsv(activity.planProduct || ''),
              escapeCsv(activity.actualProduct || ''),
              escapeCsv(activity.planHrs || ''),
              escapeCsv(activity.actualHrs || ''),
              escapeCsv(formatTimeVariance(activity.timeVariance)),
              // Plan Vol: show if either plan_start or plan_end is within date range
              escapeCsv(
                !activity.isPlanInRange
                  ? '-'
                  : (activity.plannedVolume != null ? activity.plannedVolume : '-')
              ),
              // Actual Vol: match UI logic - show only if actual batch exists
              escapeCsv(
                (activity.actualProduct && activity.actualProduct !== '-' && activity.actualVolume != null)
                  ? activity.actualVolume
                  : '-'
              ),
              // Vol Diff: hide for cleaning batches or when plan dates are outside date range
              escapeCsv(
                (!activity.isPlanInRange || (activity.activityType && activity.activityType.toLowerCase().includes('clean')))
                  ? '-'
                  : formatVolumeVariance(activity.volumeVariance)
              ),
              escapeCsv(activity.status || ''),
              escapeCsv(activity.batchMatch === false ? 'Batch mismatch' : ''),
              escapeCsv(activity.planStartDate || ''),
              escapeCsv(activity.planEndDate || ''),
              escapeCsv(activity.actualStartTime || ''),
              escapeCsv(activity.actualEndTime || activity.isOngoing ? 'Ongoing' : ''),
              escapeCsv(activity.activityType || '')
            ];
            csvRows.push(row.join(','));
          });
        } else {
          // If no activities, still show equipment with unutilized time
          const row = [
            escapeCsv(equipmentName),
            escapeCsv(unutilizedTime),
            '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''
          ];
          csvRows.push(row.join(','));
        }
        
        // Add empty row between equipment for readability
        csvRows.push('');
      });
      
      // Create CSV content
      const csvContent = csvRows.join('\n');
      
      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with date range
      const fileStartDate = dateTimeRange[0].format('YYYY-MM-DD');
      const fileEndDate = dateTimeRange[1].format('YYYY-MM-DD');
      const filename = `Consolidated_Tracker_${fileStartDate}_to_${fileEndDate}.csv`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success('Excel file downloaded successfully!');
    } catch (err) {
      // console.error('Error exporting to Excel:', err);
      message.error(`Failed to export: ${err.message || 'Unknown error'}`);
    } finally {
      setExporting(false);
    }
  }, [dateTimeRange, data, filterProject]);

  // Handle opening a single PDF from the list
  const handleOpenPdf = useCallback(async (pdfUrl, index) => {
    if (!pdfUrl) {
      message.warning('PDF URL not available');
      return;
    }

    setOpeningPdfIndex(index);
    try {
      // Extract blob file name from pdf_url (last part after /)
      const blobName = pdfUrl.split('/').pop();

      // Fetch the actual file from blob storage
      const fileBlob = await fetchFileFromBlob(blobName);

      // Ensure the blob is a valid PDF
      const pdfBlob = fileBlob.type === 'application/pdf'
        ? fileBlob
        : new Blob([fileBlob], { type: 'application/pdf' });

      // Open PDF in new tab
      const url = window.URL.createObjectURL(pdfBlob);
      const newTab = window.open(url, '_blank');

      // Check if popup was blocked
      if (!newTab) {
        Modal.warning({
          title: 'Popup Blocked',
          content: 'Please allow popups for this site to view the PDF document.',
          okText: 'OK',
          centered: true
        });
        window.URL.revokeObjectURL(url);
        return;
      }

      // Cleanup after a delay to allow the new tab to load
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);

      message.success('PDF opened successfully!');
    } catch (err) {
      Modal.error({
        title: 'Error Loading PDF',
        content: `Failed to load PDF: ${err.message || 'Unknown error'}`,
        okText: 'OK',
        centered: true
      });
    } finally {
      setOpeningPdfIndex(null);
    }
  }, []);

  // Handle batch number click - fetch document and show PDF list modal
  const handleBatchNumberClick = useCallback(async (batchNumber, equipmentCodeNumber) => {
    if (!batchNumber || batchNumber === '-') {
      message.info('No batch number available');
      return;
    }

    if (!equipmentCodeNumber || equipmentCodeNumber === '-') {
      message.info('No equipment code available');
      return;
    }

    setLoadingPdf(true);
    try {
      // Fetch planner document info with both params
      const docResponse = await fetchPlannerDocument(equipmentCodeNumber, batchNumber, true);
      
      // Check if data is null or empty array (no document found)
      if (!docResponse.success || !docResponse.data || (Array.isArray(docResponse.data) && docResponse.data.length === 0)) {
        Modal.info({
          title: 'No Document Found',
          content: `No planner document is available for batch number: ${batchNumber} and equipment code: ${equipmentCodeNumber}`,
          okText: 'OK',
          centered: true
        });
        return;
      }

      // docResponse.data is now an array of objects with pdf_url property
      const pdfArray = Array.isArray(docResponse.data) ? docResponse.data : [docResponse.data];
      
      // Filter out items without pdf_url
      const validPdfs = pdfArray.filter(item => item && item.pdf_url);
      
      if (validPdfs.length === 0) {
        Modal.warning({
          title: 'No PDFs Available',
          content: 'No PDF documents found in the response',
          okText: 'OK',
          centered: true
        });
        return;
      }

      // Set state and show modal
      setPdfList(validPdfs);
      setCurrentBatchInfo({ batchNumber, equipmentCode: equipmentCodeNumber });
      setPdfListModalVisible(true);
      
    } catch (err) {
      Modal.error({
        title: 'Error Loading Document',
        content: `Failed to load document for batch ${batchNumber}: ${err.message || 'Unknown error'}`,
        okText: 'OK',
        centered: true
      });
    } finally {
      setLoadingPdf(false);
    }
  }, []);

  return (
    <div style={{ 
      padding: '20px', 
      fontFamily: 'system-ui, -apple-system, sans-serif',
      backgroundColor: '#f9fafb',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ 
        marginBottom: '24px',
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>
              📊 Consolidated Project Tracker
            </h1>
            <span style={{
              backgroundColor: '#f59e0b',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600',
              textTransform: 'uppercase'
            }}>
              Draft
            </span>
          </div>
          <a 
            href="https://staging.nucleus.plus" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              color: '#3b82f6',
              fontSize: '14px',
              fontWeight: '500',
              textDecoration: 'none'
            }}
            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
          >
            Staging Nucleus →
          </a>
        </div>
        
        <div style={{ color: '#6b7280', fontSize: '14px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span>From: {formatDate(dateTimeRange?.[0])} {formatTime(dateTimeRange?.[0])} → To: {formatDate(dateTimeRange?.[1])} {formatTime(dateTimeRange?.[1])}</span>
          {apiMeta && (
            <span style={{ 
              backgroundColor: '#e0e7ff', 
              color: '#3730a3', 
              padding: '2px 8px', 
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500'
            }}>
              Plan v{apiMeta.plan_version_used} {apiMeta.plan_version_used === apiMeta.latest_version ? '(Latest)' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Controls */}
      <div style={{ 
        marginBottom: '24px',
        backgroundColor: 'white',
        padding: '16px 20px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: '20px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        {/* Date/Time Range Picker */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>Date Range:</label>
          <RangePicker
            showTime={{ format: 'HH:mm', minuteStep: 5 }}
            format="DD-MMM-YY HH:mm"
            value={dateTimeRange}
            onChange={(dates) => setDateTimeRange(dates)}
            style={{ minWidth: '340px' }}
            allowClear={false}
            presets={[
              { label: 'Last 24 Hours', value: [dayjs().subtract(24, 'hours'), dayjs()] },
              { label: 'Last 7 Days', value: [dayjs().subtract(7, 'day').startOf('day'), dayjs()] },
              { label: 'Last 30 Days', value: [dayjs().subtract(30, 'day').startOf('day'), dayjs()] },
              { label: 'This Week', value: [dayjs().startOf('week'), dayjs()] },
              { label: 'This Month', value: [dayjs().startOf('month'), dayjs()] },
            ]}
          />
        </div>
        {/* <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>Filter Project:</label>
          <select
            value={filterProject}
            onChange={(e) => setFilterProject(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none',
              backgroundColor: 'white'
            }}
          >
            <option value="all">All Projects</option>
            <option value="compression">Compression Only</option>
            <option value="coating">Coating Only</option>
          </select>
        </div> */}
        {/* Total Hours Chip */}
        {totalHours !== null && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '6px 12px',
            backgroundColor: '#f97316',
            color: 'white',
            borderRadius: '16px',
            fontSize: '13px',
            fontWeight: '600',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
          }}>
            {totalHours}hrs
          </div>
        )}
        <button
          onClick={fetchData}
          disabled={loading}
          style={{
            padding: '8px 16px',
            backgroundColor: loading ? '#9ca3af' : '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          {loading ? '⏳ Loading...' : '🔄 Refresh Data'}
        </button>
        <Button
          type="primary"
          icon={<span>📥</span>}
          onClick={handleExportToExcel}
          disabled={exporting || loading || !dateTimeRange || !dateTimeRange[0] || !dateTimeRange[1]}
          loading={exporting}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: '#10b981',
            borderColor: '#10b981'
          }}
        >
          {exporting ? 'Exporting...' : 'Export to Excel'}
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          marginBottom: '24px',
          backgroundColor: '#fee2e2',
          border: '1px solid #ef4444',
          borderRadius: '8px',
          padding: '16px',
          color: '#991b1b'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Table */}
      <div style={{ 
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '24px'
      }}>
        <div style={{ 
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 280px)'
        }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'separate',
            borderSpacing: 0,
            fontSize: '13px'
          }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 20 }}>
              <tr>
                <th style={{
                  backgroundColor: '#1f2937',
                  color: 'white',
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontWeight: '600',
                  borderBottom: '2px solid #374151',
                  position: 'sticky',
                  left: 0,
                  top: 0,
                  zIndex: 30,
                  minWidth: '60px'
                }}>
                  Equipment
                </th>
                <th style={{
                  backgroundColor: '#1f2937',
                  color: 'white',
                  padding: '12px 8px',
                  textAlign: 'center',
                  fontWeight: '600',
                  borderBottom: '2px solid #374151',
                  position: 'sticky',
                  top: 0,
                  minWidth: '80px'
                }}>
                  Unaccounted Time
                </th>
                <th style={{
                  backgroundColor: '#1f2937',
                  color: 'white',
                  padding: '8px',
                  textAlign: 'center',
                  fontWeight: '600',
                  borderBottom: '2px solid #374151',
                  position: 'sticky',
                  top: 0,
                  minWidth: '60px'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                    <span>Plan Batch</span>
                    <Select
                      size="small"
                      value={selectedPlanVersion}
                      onChange={(value) => setSelectedPlanVersion(value)}
                      loading={loadingVersions}
                      style={{ 
                        width: '130px',
                        fontSize: '11px'
                      }}
                      dropdownStyle={{ minWidth: '180px' }}
                      options={[
                        { 
                          value: null, 
                          label: `Latest (v${apiMeta?.latest_version || planVersions[0]?.plan_version || '?'})` 
                        },
                        ...planVersions.map(v => ({
                          value: v.plan_version,
                          label: `v${v.plan_version} (${dayjs(v.published_at).format('DD-MMM HH:mm')})`
                        }))
                      ]}
                    />
                  </div>
                </th>
                <th style={{
                  backgroundColor: '#1f2937',
                  color: 'white',
                  padding: '12px 8px',
                  textAlign: 'center',
                  fontWeight: '600',
                  borderBottom: '2px solid #374151',
                  position: 'sticky',
                  top: 0,
                  minWidth: '80px'
                }}>
                  Actual Batch
                </th>
                <th style={{
                  backgroundColor: '#1f2937',
                  color: 'white',
                  padding: '12px 8px',
                  textAlign: 'center',
                  fontWeight: '600',
                  borderBottom: '2px solid #374151',
                  position: 'sticky',
                  top: 0,
                  minWidth: '70px'
                }}>
                  Plan Hrs
                </th>
                <th style={{
                  backgroundColor: '#1f2937',
                  color: 'white',
                  padding: '12px 8px',
                  textAlign: 'center',
                  fontWeight: '600',
                  borderBottom: '2px solid #374151',
                  position: 'sticky',
                  top: 0,
                  minWidth: '70px'
                }}>
                  Actual Hrs
                </th>
                <th style={{
                  backgroundColor: '#1f2937',
                  color: 'white',
                  padding: '12px 8px',
                  textAlign: 'center',
                  fontWeight: '600',
                  borderBottom: '2px solid #374151',
                  position: 'sticky',
                  top: 0,
                  minWidth: '70px'
                }}>
                  Time Diff
                </th>
                <th style={{
                  backgroundColor: '#1f2937',
                  color: 'white',
                  padding: '12px 8px',
                  textAlign: 'center',
                  fontWeight: '600',
                  borderBottom: '2px solid #374151',
                  position: 'sticky',
                  top: 0,
                  minWidth: '70px'
                }}>
                  Plan Vol
                </th>
                <th style={{
                  backgroundColor: '#1f2937',
                  color: 'white',
                  padding: '12px 8px',
                  textAlign: 'center',
                  fontWeight: '600',
                  borderBottom: '2px solid #374151',
                  position: 'sticky',
                  top: 0,
                  minWidth: '70px'
                }}>
                  Actual Vol
                </th>
                <th style={{
                  backgroundColor: '#1f2937',
                  color: 'white',
                  padding: '12px 8px',
                  textAlign: 'center',
                  fontWeight: '600',
                  borderBottom: '2px solid #374151',
                  position: 'sticky',
                  top: 0,
                  minWidth: '70px'
                }}>
                  Vol Diff
                </th>
                {/* Status column hidden
                <th style={{
                  backgroundColor: '#1f2937',
                  color: 'white',
                  padding: '12px 8px',
                  textAlign: 'center',
                  fontWeight: '600',
                  borderBottom: '2px solid #374151',
                  position: 'sticky',
                  top: 0,
                  minWidth: '80px'
                }}>
                  Status
                </th>
                */}
                <th style={{
                  backgroundColor: '#1f2937',
                  color: 'white',
                  padding: '12px 8px',
                  textAlign: 'left',
                  fontWeight: '600',
                  borderBottom: '2px solid #374151',
                  position: 'sticky',
                  top: 0,
                  minWidth: '150px'
                }}>
                  Remarks
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="11" style={{ padding: '48px', textAlign: 'center' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: '16px', color: '#6b7280' }}>Loading activities...</div>
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan="11" style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>
                    No activities found for the selected date range
                  </td>
                </tr>
              ) : filteredData.map((project, projectIdx) => {
                //console.log("project ",projectIdx,project);
                return (
                <React.Fragment key={`equipment-${projectIdx}`}>
                      {project.activities.length > 0 ? (
                        project.activities.map((activity, activityIdx) => {

                          // console.log("activity ",activityIdx,activity,activity.planProduct,activity.plannedProductATreportUrl);
                          // Status-based styling
                          // const getStatusStyle = (status) => {
                          //   switch(status) {
                          //     case 'matched':
                          //       return { backgroundColor: '#dcfce7', color: '#166534' };
                          //     case 'not_started':
                          //       return { backgroundColor: '#fef3c7', color: '#92400e' };
                          //     case 'unplanned':
                          //       return { backgroundColor: '#fee2e2', color: '#991b1b' };
                          //     default:
                          //       return {};
                          //   }
                          // };
                          
                          // const getStatusLabel = (status) => {
                          //   switch(status) {
                          //     case 'matched': return 'Matched';
                          //     case 'not_started': return 'Not Started';
                          //     case 'unplanned': return 'Unplanned';
                          //     default: return status || '-';
                          //   }
                          // };

                          const getRowBackground = () => {
                            if (activity.status === 'not_started') return 'rgba(254, 243, 199, 0.3)';
                            if (activity.status === 'unplanned') return 'rgba(254, 226, 226, 0.3)';
                            if (activity.isOngoing) return 'rgba(59, 130, 246, 0.1)';
                            return projectIdx % 2 === 0 ? '#ffffff' : '#f9fafb';
                          };

                          return (
                            <tr 
                              key={`${projectIdx}-${activityIdx}`}
                              style={{ backgroundColor: getRowBackground() }}
                            >
                              {activityIdx === 0 && (
                                <>
                                  <td
                                    rowSpan={project.activities.length}
                                    style={{
                                      padding: '12px 16px',
                                      fontWeight: '600',
                                      backgroundColor: projectIdx % 2 === 0 ? '#f3f4f6' : '#e5e7eb',
                                      borderBottom: '1px solid #e5e7eb',
                                      borderRight: '2px solid #e5e7eb',
                                      borderLeft: `3px solid ${groupConfig[getEquipmentCategory(project.project)]?.color || '#6b7280'}`,
                                      position: 'sticky',
                                      left: 0,
                                      zIndex: 5,
                                      whiteSpace: 'pre-line',
                                      verticalAlign: 'top',
                                      fontSize: '12px',
                                      lineHeight: '1.4'
                                    }}
                                  >
                                    {getEquipmentCategory(project.project)} - {project.project}
                                  </td>
                                  <td
                                    rowSpan={project.activities.length}
                                    style={{
                                      padding: '12px 8px',
                                      textAlign: 'center',
                                      fontWeight: '600',
                                      backgroundColor: projectIdx % 2 === 0 ? '#ffffff' : '#f9fafb',
                                      borderBottom: '1px solid #e5e7eb',
                                      color: project.unutilizedTime && project.unutilizedTime !== '00:00' ? 'rgb(153, 27, 27)' : '#6b7280',
                                      fontSize: '12px'
                                    }}
                                  >
                                    {project.unutilizedTime || '-'}
                                  </td>
                                </>
                              )}
                          <td style={{
                            padding: '8px 10px',
                            borderBottom: '1px solid #e5e7eb',
                            textAlign: 'center',
                            fontSize: '12px',
                            backgroundColor: !activity.batchMatch && activity.status === 'matched' && activity.planProduct && activity.actualProduct 
                              ? 'rgba(239, 68, 68, 0.1)' 
                              : 'transparent'
                          }}>
                            {/* Only make manufacturing batch numbers clickable, not cleaning/maintenance */}
                            {activity.activityType?.toLowerCase() === 'manufacturing' && activity.planProduct && activity.planProduct !== '-'&& activity?.plannedProductATreportUrl.length>0 ? (
                              <span
                                onClick={() => handleBatchNumberClick(activity.planProduct, project.equipmentCodeNumber)}
                            style={{
  cursor: activity?.plannedProductATreportUrl.length>0 ? 'pointer' : 'default',
  color: activity?.plannedProductATreportUrl.length>0 ? '#3b82f6' : '#6b7280',
  textDecoration: activity?.plannedProductATreportUrl.length>0 ? 'underline' : 'none',
  fontWeight: activity?.plannedProductATreportUrl.length>0 ? 500 : 400,
  padding: '2px 4px',
  borderRadius: '2px',
  transition: 'all 0.2s'
}}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                }}
                                title={`Click to view document for ${activity.planProduct}`}
                              >
                                {activity.planProduct}
                              </span>
                            ) : (
                              <span style={{ color: '#6b7280' }}>
                                {activity.planProduct || '-'}
                              </span>
                            )}
                          </td>
                          <td style={{
                            padding: '8px 10px',
                            borderBottom: '1px solid #e5e7eb',
                            textAlign: 'center',
                            fontSize: '12px',
                            backgroundColor: !activity.batchMatch && activity.status === 'matched' && activity.planProduct && activity.actualProduct 
                              ? 'rgba(239, 68, 68, 0.1)' 
                              : 'transparent'
                          }}>
                            {/* Only make manufacturing batch numbers clickable, not cleaning/maintenance */}
                            {activity.activityType?.toLowerCase() === 'manufacturing' && activity.actualProduct && activity.actualProduct !== '-' && activity?.actualProductATreportUrl.length>0 ? (
                              <span
                                onClick={() => handleBatchNumberClick(activity.actualProduct,project.equipmentCodeNumber)}
                               style={{
  cursor: activity?.actualProductATreportUrl.length>0 ? 'pointer' : 'default',
  color: activity?.actualProductATreportUrl.length>0 ? '#3b82f6' : '#6b7280',
  textDecoration: activity?.actualProductATreportUrl.length>0 ? 'underline' : 'none',
  fontWeight: activity?.actualProductATreportUrl.length>0 ? 500 : 400,
  padding: '2px 4px',
  borderRadius: '2px',
  transition: 'all 0.2s'
}}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                }}
                                title={`Click to view document for ${activity.actualProduct}`}
                              >
                                {activity.actualProduct}
                              </span>
                            ) : (
                              <span style={{ color: '#6b7280' }}>
                                {activity.actualProduct || '-'}
                              </span>
                            )}
                          </td>
                          <td style={{
                            padding: '8px',
                            borderBottom: '1px solid #e5e7eb',
                            textAlign: 'center',
                            fontSize: '12px'
                          }}>
                            {activity.planHrs || '-'}
                          </td>
                          <td style={{
                            padding: '8px',
                            borderBottom: '1px solid #e5e7eb',
                            textAlign: 'center',
                            fontSize: '12px'
                          }}>
                            {activity.actualHrs || '-'}
                          </td>
                          <td
                            style={{
                              padding: '8px',
                              borderBottom: '1px solid #e5e7eb',
                              textAlign: 'center',
                              fontSize: '12px',
                              fontWeight: '600',
                              ...getVarianceStyle(activity.timeVariance)
                            }}
                          >
                            {formatVariance(activity.timeVariance)}
                          </td>
                          <td style={{
                            padding: '8px',
                            borderBottom: '1px solid #e5e7eb',
                            textAlign: 'center',
                            fontSize: '12px'
                          }}>
                            {/* Plan Vol: show if either plan_start or plan_end is within selected date range */}
                            {!activity.isPlanInRange
                              ? '-'
                              : (activity.plannedVolume != null
                                  ? activity.plannedVolume
                                  : '-')
                            }
                          </td>
                          <td style={{
                            padding: '8px',
                            borderBottom: '1px solid #e5e7eb',
                            textAlign: 'center',
                            fontSize: '12px'
                          }}>
                            {/* Show Actual Vol only if batch number exists, else '-' */}
                            {(activity.actualProduct && activity.actualProduct !== '-' && activity.actualVolume != null)
                              ? activity.actualVolume
                              : '-'}
                          </td>
                          <td
                            style={{
                              padding: '8px',
                              borderBottom: '1px solid #e5e7eb',
                              textAlign: 'center',
                              fontSize: '12px',
                              fontWeight: '600',
                              ...getVarianceStyle(activity.status === 'not_started' ? 0 : activity.volumeVariance)
                            }}
                          >
                            {/* Show '-' for cleaning batches or when plan dates are outside the selected date range */}
                            {(!activity.isPlanInRange || (activity.activityType && activity.activityType.toLowerCase().includes('clean')))
                              ? '-'
                              : formatVariance(activity.volumeVariance)}
                          </td>
                          {/* Status column hidden
                          <td style={{
                            padding: '8px',
                            borderBottom: '1px solid #e5e7eb',
                            textAlign: 'center',
                            fontSize: '11px'
                          }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontWeight: '500',
                              ...(activity.status === 'matched' 
                                ? { backgroundColor: '#dcfce7', color: '#166534' }
                                : activity.status === 'not_started'
                                ? { backgroundColor: '#fef3c7', color: '#92400e' }
                                : activity.status === 'unplanned'
                                ? { backgroundColor: '#fee2e2', color: '#991b1b' }
                                : {})
                            }}>
                              {getStatusLabel(activity.status)}
                            </span>
                          </td>
                          */}
                          <td style={{
                            padding: '8px 12px',
                            borderBottom: '1px solid #e5e7eb',
                            textAlign: 'left',
                            fontSize: '12px',
                            color: '#6b7280',
                            maxWidth: '150px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={activity.remarks || ''}
                          >
                            {activity.remarks || '-'}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr style={{ 
                      backgroundColor: projectIdx % 2 === 0 ? '#ffffff' : '#f9fafb'
                    }}>
                      <td
                        style={{
                          padding: '12px 16px',
                          fontWeight: '600',
                          backgroundColor: projectIdx % 2 === 0 ? '#f3f4f6' : '#e5e7eb',
                          borderBottom: '1px solid #e5e7eb',
                          borderRight: '2px solid #e5e7eb',
                          borderLeft: `3px solid ${groupConfig[getEquipmentCategory(project.project)]?.color || '#6b7280'}`,
                          position: 'sticky',
                          left: 0,
                          zIndex: 5,
                          whiteSpace: 'pre-line',
                          verticalAlign: 'top',
                          fontSize: '12px',
                          lineHeight: '1.4'
                        }}
                      >
                        {getEquipmentCategory(project.project)} - {project.project}
                      </td>
                      <td
                        style={{
                          padding: '12px 8px',
                          textAlign: 'center',
                          fontWeight: '600',
                          backgroundColor: projectIdx % 2 === 0 ? '#ffffff' : '#f9fafb',
                          borderBottom: '1px solid #e5e7eb',
                          color: project.unutilizedTime && project.unutilizedTime !== '00:00' ? 'rgb(153, 27, 27)' : '#6b7280',
                          fontSize: '12px'
                        }}
                      >
                        {project.unutilizedTime || '-'}
                      </td>
                      <td colSpan="8" style={{
                        padding: '12px',
                        textAlign: 'center',
                        color: '#9ca3af',
                        fontStyle: 'italic',
                        borderBottom: '1px solid #e5e7eb'
                      }}>
                        No activities
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )})}
            </tbody>
          </table>
        </div>
      </div>

      {/* PDF List Modal */}
      <Modal
        title={`PDF Documents - Batch: ${currentBatchInfo.batchNumber}`}
        open={pdfListModalVisible}
        onCancel={() => setPdfListModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPdfListModalVisible(false)}>
            Close
          </Button>
        ]}
        centered
        width={500}
      >
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {pdfList.length === 0 ? (
            <p style={{ color: '#6b7280', textAlign: 'center' }}>No PDF documents available</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {pdfList.map((item, index) => (
                <li 
                  key={index} 
                  style={{ 
                    padding: '12px 16px', 
                    borderBottom: index < pdfList.length - 1 ? '1px solid #e5e7eb' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <span style={{ 
                    color: '#374151', 
                    fontSize: '14px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '300px'
                  }}>
                    {item.pdf_url ? item.pdf_url.split('/').pop() : `Document ${index + 1}`}
                  </span>
                  <Button
                    type="link"
                    onClick={() => handleOpenPdf(item.pdf_url, index)}
                    disabled={openingPdfIndex !== null}
                    loading={openingPdfIndex === index}
                    style={{ padding: '0 8px', color: openingPdfIndex === index ? '#9ca3af' : '#3b82f6' }}
                  >
                    {openingPdfIndex === index ? 'Loading...' : 'Open PDF →'}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Modal>

      {/* Loading Overlay for batch number click */}
      {loadingPdf && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px 32px',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}>
            <Spin size="large" />
            <span style={{ color: '#374151', fontSize: '14px' }}>Loading documents...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsolidatedTracker;

